import { useEffect, useState } from 'react'
import {
  api, ApiError,
  type PermissionBranch, type Role,
} from '../lib/api'

/** Roles, and who holds them.
 *
 *  PLAN.html §19: "There are no fixed dispatcher ranks. You create named
 *  roles, tick what each one can reach, and assign a role to each user. Change
 *  the role once and everyone holding it updates."
 *
 *  THE TREE COMES FROM THE SERVER
 *
 *  Not from a copy in here. §19's rule is that the tree mirrors the interface
 *  exactly and "no permission is ever invented in a spot that doesn't exist on
 *  screen" — so the list of checkboxes is the list of things a route actually
 *  checks. A node added to app/permissions.py appears here with no change on
 *  this side, and a checkbox can never exist for a feature nobody built.
 *
 *  THE ADMINISTRATOR SHOWS EVERYTHING TICKED AND DISABLED
 *
 *  It holds the wildcard, which means "everything, including what does not
 *  exist yet". That cannot be represented as ticks and must not be edited into
 *  a fixed list by accident — doing so would silently narrow it the next time
 *  a node was added. So it renders as read-only, with a sentence saying why. */

function Ticks({
  tree, checked, onChange, disabled,
}: {
  tree: PermissionBranch[]
  checked: Set<string>
  onChange: (next: Set<string>) => void
  disabled?: boolean
}) {
  function toggle(key: string, on: boolean) {
    const next = new Set(checked)
    if (on) next.add(key)
    else next.delete(key)
    onChange(next)
  }

  function toggleBranch(branch: PermissionBranch, on: boolean) {
    const next = new Set(checked)
    const keys = [branch.key, ...branch.groups.flatMap((g) => g.nodes.map((n) => n.key))]
    for (const k of keys) {
      if (on) next.add(k)
      else next.delete(k)
    }
    onChange(next)
  }

  return (
    <div className="permtree">
      {tree.map((branch) => {
        const leaves = branch.groups.flatMap((g) => g.nodes.map((n) => n.key))
        const on = leaves.filter((k) => checked.has(k)).length
        return (
          <div className="permbranch" key={branch.key}>
            <label className="permhead">
              <input
                type="checkbox"
                disabled={disabled}
                checked={on === leaves.length && leaves.length > 0}
                // Part-ticked is its own state. Without it a branch with three
                // of five leaves looks identical to one with none.
                ref={(el) => {
                  if (el) el.indeterminate = on > 0 && on < leaves.length
                }}
                onChange={(e) => toggleBranch(branch, e.target.checked)}
              />
              <b>{branch.label}</b>
              <small>{branch.blurb}</small>
            </label>
            {branch.groups.map((group) => (
              <div className="permgroup" key={group.key}>
                {branch.groups.length > 1 && (
                  <div className="permgrouphead">{group.label}</div>
                )}
                {group.nodes.map((node) => (
                  <label className="permnode" key={node.key}>
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={checked.has(node.key)}
                      onChange={(e) => toggle(node.key, e.target.checked)}
                    />
                    <span>{node.label}</span>
                    {node.note && <em>{node.note}</em>}
                  </label>
                ))}
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

export function Roles() {
  const [roles, setRoles] = useState<Role[]>([])
  const [tree, setTree] = useState<PermissionBranch[]>([])
  const [wildcard, setWildcard] = useState('*')
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState<Set<string>>(new Set())
  const [name, setName] = useState('')
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      const res = await api.roles()
      setRoles(res.roles)
      // `res.users` is deliberately not read. Who holds which role is answered
      // by the Users section above this one, which is also where it is
      // changed -- see the note where "Who holds what" used to be.
      setTree(res.tree)
      setWildcard(res.wildcard)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load roles.')
    }
  }

  useEffect(() => { load() }, [])

  function open(role: Role) {
    setEditing(role.id)
    setDraft(new Set(role.permissions))
    setName(role.name)
    setAdding(false)
  }

  async function save(role: Role) {
    setBusy(true)
    try {
      await api.roleUpdate(role.id, {
        name, permissions: [...draft],
      })
      setEditing(null)
      await load()
      setError(null)
    } catch (e) {
      // The lock-out refusal lands here, and it is the one message on this
      // screen somebody must actually read.
      setError(e instanceof ApiError ? e.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  async function create() {
    setBusy(true)
    try {
      await api.roleCreate({
        name, permissions: [...draft],
      })
      setAdding(false)
      setName('')
      setDraft(new Set())
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create the role.')
    } finally {
      setBusy(false)
    }
  }

  async function remove(role: Role) {
    if (!confirm(`Remove the role "${role.name}"?`)) return
    try {
      await api.roleDelete(role.id)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not remove it.')
    }
  }

  return (
    <>
      {error && <div className="notice">{error}</div>}

      {roles.map((role) => {
        const wild = role.permissions.includes(wildcard)
        return (
          <div className="rolecard" key={role.id}>
            <div className="rolehead">
              <b>{role.name}</b>
              {role.is_system && <span className="pill">built in</span>}
              <span className="rolecount">
                {role.holders} {role.holders === 1 ? 'login' : 'logins'}
              </span>
              {/* The panel level is NOT shown or edited here. It is a
                  property of the SEAT ("Dispatcher 1"), set in the super admin
                  console, and `roles.dispatch_level` is read by nothing --
                  a control here would have let somebody believe he had
                  restricted a role when he had changed a column nobody reads. */}
              <button className="ghost tiny nogrow"
                      onClick={() => (editing === role.id ? setEditing(null) : open(role))}>
                {editing === role.id ? 'Cancel' : wild ? 'View' : 'Edit'}
              </button>
              {!role.is_system && role.holders === 0 && (
                <button className="ghost tiny nogrow" onClick={() => remove(role)}>
                  Remove
                </button>
              )}
            </div>
            {role.description && <p className="footnote">{role.description}</p>}

            {editing === role.id && (
              <div className="roleedit">
                {wild ? (
                  <p className="footnote">
                    This role holds everything, including parts of the product
                    that do not exist yet. That cannot be shown as ticks, and
                    turning it into a fixed list would quietly narrow it the
                    next time something is added — so it is not editable here.
                    Build a new role instead and move people to it.
                  </p>
                ) : (
                  <>
                    <div className="field">
                      <label>Name</label>
                      <input value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                  </>
                )}
                <Ticks tree={tree} checked={wild ? new Set(allKeys(tree)) : draft}
                       onChange={setDraft} disabled={wild} />
                {!wild && (
                  <div className="btnrow">
                    <button className="primary compact" disabled={busy}
                            onClick={() => save(role)}>
                      {busy ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}

      {adding ? (
        <div className="rolecard">
          <div className="field">
            <label>Name</label>
            <input value={name} placeholder="Shift supervisor"
                   onChange={(e) => setName(e.target.value)} />
          </div>
          <Ticks tree={tree} checked={draft} onChange={setDraft} />
          <div className="btnrow">
            <button className="primary compact" disabled={busy || !name.trim()}
                    onClick={create}>
              {busy ? 'Adding…' : 'Add role'}
            </button>
            <button className="ghost" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="btnrow">
          <button className="primary compact"
                  onClick={() => { setAdding(true); setEditing(null); setName(''); setDraft(new Set()) }}>
            Add a role
          </button>
        </div>
      )}

      {/* "WHO HOLDS WHAT" WAS HERE and is gone, at the customer's request:
          it listed every login with a role picker, which the Users section
          directly above this one now does — and a second place to change
          somebody's role is a second place for the two to disagree about who
          is an administrator.

          The count on each role card above ("1 login") is unaffected: it comes
          from `role.holders`, which the server counts. */}
    </>
  )
}

function allKeys(tree: PermissionBranch[]): string[] {
  return tree.flatMap((b) => [b.key, ...b.groups.flatMap((g) => g.nodes.map((n) => n.key))])
}
